import { defineStore } from 'pinia'
import { useConfigStore } from './configStore'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import type { Ref } from 'vue'
import { useIntervalFn, useNow } from '@vueuse/core'
import type { User } from '@dashboard/types/user'

export type PlanTier = 'grow' | 'scale' | 'pro' | 'legacy' | 'legacy_cta'

export const useSubscriptionStore = defineStore('subscription', () => {
  const { owner } = storeToRefs(useConfigStore())
  return useBillingState(owner)
})

/**
 * Plan and billing state of one billing owner. The subscription store applies
 * it to the open project's owner; Account › Billing applies it to the
 * signed-in user.
 */
export function useBillingState(owner: Readonly<Ref<User | null | undefined>>) {
  const now = useNow({ scheduler: (cb) => useIntervalFn(cb, 1000) })
  const trialActivation = computed(() => owner.value?.trial_activation ?? null)
  /** Trial days this project's own subscription would start with; null when starting it charges at once. */
  const projectTrialDays = computed(() => owner.value?.project_trial_days ?? null)
  const hasPendingActivation = computed(() => !!trialActivation.value?.pending_activation)
  const isNonRenewingTrial = computed(() => trialActivation.value !== null)
  const fixedTrialExpired = computed(
    () =>
      !!trialActivation.value &&
      (trialActivation.value.charge_now || now.value.getTime() >= trialActivation.value.trial_end * 1000)
  )

  // Computed properties to access owner attributes
  const stripeStatus = computed(() => owner.value?.stripe_status)
  const trialEndsAt = computed(() => owner.value?.trial_ends_at)
  const isFreeAccount = computed(() => owner.value?.free_account)
  const isAdmin = computed(() => owner.value?.is_admin)
  const activeSubscription = computed(() => owner.value?.active_subscription ?? null)
  const resolvedSubscriptionStatus = computed(
    () => activeSubscription.value?.stripe_status ?? stripeStatus.value ?? null
  )

  const hasValidSubscription = computed(() => {
    const subscription = activeSubscription.value
    if (!subscription) {
      return false
    }
    if (isNonRenewingTrial.value) {
      return false
    }

    if (resolvedSubscriptionStatus.value !== 'active') {
      return false
    }
    return !subscription.ends_at
  })

  // =========================================================================
  // Tier-aware plan properties (ENG-855)
  // =========================================================================

  /** Current plan tier, including the legacy CTA catalog without campaigns */
  const planTier = computed<PlanTier>(() => owner.value?.plan?.tier ?? 'grow')

  /** Full list of features available to the user (already merged by tier on backend) */
  const planFeatures = computed<string[]>(() => owner.value?.plan?.features ?? [])

  /** ESP providers available to the user */
  const planEspProviders = computed<string[]>(() => owner.value?.plan?.esp_providers ?? [])

  /** Project and collaborator limits with current usage */
  const planLimits = computed(
    () =>
      owner.value?.plan?.limits ?? {
        projects: { current: 0, max: 1 },
        collaborators: { current: 0, max: 1 }
      }
  )

  /** Billing model: 'legacy' (single subscription covers all teams) or 'per_team' (each project has its own subscription) */
  const billingModel = computed<'legacy' | 'per_team'>(() => owner.value?.billing_model ?? 'per_team')

  /** Whether this user is on the legacy billing model (single subscription covers all teams) */
  const isLegacyBilling = computed(() => billingModel.value === 'legacy')

  /** Whether this user is on per-team billing (each project needs its own subscription) */
  const isPerTeamBilling = computed(() => billingModel.value === 'per_team')

  /**
   * Which pricing the user signed up under: 'v1' (old visitor-based) or 'v2' (new feature-based).
   * NULL defaults to 'v1', matching the backend. This is pricing history, not entitlement.
   */
  const pricingVersion = computed(() => owner.value?.pricing_version ?? 'v1')

  /** Whether this user signed up under the old pricing model */
  const isLegacyPricing = computed(() => pricingVersion.value === 'v1')

  /** Whether the resolved plan is on the legacy feature catalog */
  const isLegacy = computed(() => planTier.value === 'legacy' || planTier.value === 'legacy_cta')

  /** Free builder (reverse trial: signed up but hasn't published yet) */
  const isFreeBuilder = computed(() => owner.value?.is_free_builder === true)

  /** Lapsed project that must resubscribe before using the app */
  const requiresReactivation = computed(() => owner.value?.requires_reactivation === true)
  const requiresPaymentRecovery = computed(() => owner.value?.requires_payment_recovery || false)

  /** Complimentary accounts and free builders have unrestricted feature access.
   *  Paid legacy plans use the backend feature list, including CTA's campaign exclusion. */
  const hasUnrestrictedAccess = computed(() => isFreeBuilder.value || isFreeAccount.value || isAdmin.value)

  /** Check if user has access to a specific plan feature */
  const hasFeature = (feature: string): boolean => {
    if (hasUnrestrictedAccess.value) {
      return true
    }
    return planFeatures.value.includes(feature)
  }

  /** Check if user has at least the specified tier level */
  const hasTierOrHigher = (tier: PlanTier): boolean => {
    if (hasUnrestrictedAccess.value || isLegacy.value) {
      return true
    }
    const tierOrder: PlanTier[] = ['grow', 'scale', 'pro']
    const userIndex = tierOrder.indexOf(planTier.value)
    const requiredIndex = tierOrder.indexOf(tier)
    return userIndex >= requiredIndex
  }

  /**
   * Pre-subscription setup state for reverse-trial users.
   * The current-team payload should set `is_free_builder` for unpaid teams even
   * when another team already has a trial, but we keep the legacy-shaped
   * fallback for older payloads that have not been updated yet.
   */
  const isPreSubscriptionSetup = computed(() => {
    if (!owner.value) {
      return false
    }
    if (isFreeAccount.value || isAdmin.value) {
      return false
    }
    if (isNonRenewingTrial.value) {
      return false
    }
    if (requiresReactivation.value) {
      return false
    }
    if (isFreeBuilder.value) {
      return true
    }
    return !activeSubscription.value && !resolvedSubscriptionStatus.value && !owner.value.trial_ends_at
  })

  /**
   * Determines whether the user should be treated as active inside the app.
   * Active users are either:
   * 1. Free or admin accounts
   * 2. Pre-subscription reverse-trial users
   * 3. In a trial period
   * 4. Have an active Stripe subscription
   *
   * Having a card on file does not imply an active subscription.
   *
   * Returns true while owner data is loading to prevent
   * flashing "inactive" state before data is available.
   */
  const isActive = computed(() => {
    // If owner hasn't loaded yet, assume active to prevent flash of inactive state
    if (!owner.value) {
      return true
    }
    if (isPreSubscriptionSetup.value) {
      return true
    }
    if (isTrialing.value) {
      return true
    }
    if (isFreeAccount.value || isAdmin.value) {
      return true
    }
    if (requiresReactivation.value) {
      return false
    }
    if (isNonRenewingTrial.value) {
      return false
    }
    return resolvedSubscriptionStatus.value === 'active'
  })

  /**
   * Determines if the user is currently in trial period
   */
  const isTrialing = computed(() => {
    if (hasPendingActivation.value) {
      return false
    }
    if (trialActivation.value) {
      return !fixedTrialExpired.value
    }
    if (owner.value?.on_trial) {
      return true
    }
    return resolvedSubscriptionStatus.value === 'trialing'
  })

  const trialExpiresAt = computed(() => {
    if (trialActivation.value) {
      return trialActivation.value.trial_ends_at
    }
    if (owner.value?.on_trial) {
      return owner.value?.trial_ends_at
    }
    return null
  })

  /**
   * Whether the user has a payment method on file.
   * Used to distinguish between trial-expired (never paid) and payment-failed (did pay once).
   */
  const hasPaymentMethod = computed(() => owner.value?.has_payment_method ?? false)

  const isTrialExpired = computed(() => {
    if (!owner.value) {
      return false
    }
    if (isNonRenewingTrial.value) {
      return fixedTrialExpired.value && !isFreeAccount.value && !isAdmin.value
    }
    if (isActive.value) {
      return false
    }
    // If they have no payment method, they never actually subscribed
    return !owner.value.has_payment_method
  })

  /**
   * Actionable billing failure state for app UI.
   * Free accounts remain billing-exempt even when stale Stripe subscription data
   * still reports past_due or unpaid. (ENG-1167)
   */
  const isPaymentFailed = computed(() => {
    if (!owner.value) {
      return false
    }
    if (isFreeAccount.value) {
      return false
    }
    if (isTrialing.value) {
      return false
    }
    return ['past_due', 'unpaid'].includes(resolvedSubscriptionStatus.value ?? '')
  })

  /**
   * User had an active subscription that has been canceled (not payment failure).
   * Distinguished from isTrialExpired (never paid) and isPaymentFailed (past_due/unpaid).
   */
  const isCanceled = computed(() => {
    if (!owner.value) {
      return false
    }
    if (isActive.value) {
      return false
    }
    if (isPaymentFailed.value) {
      return false
    }
    if (isTrialExpired.value) {
      return false
    }
    return hasPaymentMethod.value && resolvedSubscriptionStatus.value === 'canceled'
  })

  /**
   * User has not started a paid or trialing subscription yet.
   * Free builders remain in this state even after adding a payment method,
   * because card-on-file and subscribed are separate concepts.
   */
  const hasNoSubscription = computed(() => {
    if (!owner.value) {
      return false
    }
    if (isTrialing.value) {
      return false
    }
    if (hasValidSubscription.value) {
      return false
    }
    return isPreSubscriptionSetup.value
  })

  const needsCurrentTeamBillingSetup = computed(() => {
    if (!owner.value) {
      return false
    }
    if (!isPerTeamBilling.value) {
      return false
    }
    if (isFreeAccount.value || isAdmin.value) {
      return false
    }
    return hasNoSubscription.value || hasPendingActivation.value
  })

  return {
    trialActivation,
    projectTrialDays,
    hasPendingActivation,
    isNonRenewingTrial,
    stripeStatus,
    trialEndsAt,
    trialExpiresAt,
    isFreeAccount,
    isAdmin,
    isActive,
    isTrialing,
    isTrialExpired,
    isPaymentFailed,
    isCanceled,
    hasPaymentMethod,
    hasNoSubscription,
    needsCurrentTeamBillingSetup,
    activeSubscription,
    hasValidSubscription,
    // Tier-aware properties
    planTier,
    planFeatures,
    planEspProviders,
    planLimits,
    isLegacy,
    isFreeBuilder,
    requiresReactivation,
    requiresPaymentRecovery,
    isPreSubscriptionSetup,
    hasUnrestrictedAccess,
    hasFeature,
    hasTierOrHigher,
    // Billing model
    billingModel,
    isLegacyBilling,
    isPerTeamBilling,
    // Pricing version
    pricingVersion,
    isLegacyPricing
  }
}
